import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useToasts } from '../../lib/toast';
import type { ShoppingItem } from '@taj/types';

const esc = (s: string) => (s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

export function ShoppingListManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const [items, setItems] = useState<ShoppingItem[]>([]);
  const [name, setName] = useState('');
  const [cat, setCat] = useState('');
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('');

  const load = () => api.listShopping().then(setItems).catch((e) => push(e.message, 'warn'));
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.addShopping({ name: name.trim(), category: cat.trim(), quantity: qty.trim(), unit: unit.trim() });
      setName(''); setQty(''); setUnit(''); // on garde la catégorie pour enchaîner
      load();
    } catch (e: any) { push(e.message, 'warn'); }
  };

  const patch = (id: string, p: Partial<ShoppingItem>) => {
    setItems((list) => list.map((i) => (i.id === id ? { ...i, ...p } : i)));
  };
  const save = (id: string, p: Partial<Pick<ShoppingItem, 'name' | 'category' | 'unit' | 'quantity' | 'checked'>>) =>
    api.updateShopping(id, p).catch((e) => push(e.message, 'warn'));

  // + / − : ajuste la quantité numérique d'un article (comme à la caisse).
  const step = (i: ShoppingItem, delta: number) => {
    const n = Math.max(0, (parseFloat((i.quantity || '0').replace(',', '.')) || 0) + delta);
    const q = String(n);
    patch(i.id, { quantity: q });
    save(i.id, { quantity: q });
  };

  const remove = async (id: string) => {
    try { await api.deleteShopping(id); setItems((l) => l.filter((i) => i.id !== id)); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  const uncheckAll = async () => {
    try { await api.clearShoppingChecks(); setItems((l) => l.map((i) => ({ ...i, checked: false }))); push('Tout décoché'); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  const printList = () => {
    const checked = items.filter((i) => i.checked);
    const list = checked.length ? checked : items;
    if (!list.length) { push('Liste vide', 'warn'); return; }
    const date = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const groups = new Map<string, ShoppingItem[]>();
    list.forEach((i) => { const k = i.category?.trim() || 'Autres'; if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(i); });
    const gnames = Array.from(groups.keys()).sort((a, b) => (a === 'Autres' ? 1 : b === 'Autres' ? -1 : a.localeCompare(b)));
    const rows = gnames.map((g) =>
      `<tr><td colspan="3" class="cat">${esc(g)}</td></tr>` +
      groups.get(g)!.map((i) => `<tr><td class="box">☐</td><td class="nm">${esc(i.name)}</td><td class="qt">${esc(i.quantity)} ${esc(i.unit)}</td></tr>`).join('')
    ).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Liste de courses</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:24px;max-width:640px;margin:auto;}
        h1{font-size:22px;margin:0 0 2px;}
        .date{color:#666;font-size:13px;margin-bottom:16px;text-transform:capitalize;}
        table{width:100%;border-collapse:collapse;}
        td{padding:9px 6px;border-bottom:1px solid #ddd;font-size:16px;}
        .box{width:26px;font-size:20px;}
        .nm{font-weight:700;}
        .qt{text-align:right;color:#333;white-space:nowrap;}
        .cat{font-weight:800;background:#f2f2f2;font-size:13px;color:#444;text-transform:uppercase;letter-spacing:.04em;padding-top:12px;}
        .ft{margin-top:18px;color:#999;font-size:12px;}
        @media print{.noprint{display:none;}}
      </style></head><body>
      <h1>🛒 Liste de courses — TAJ FUSION</h1>
      <div class="date">${esc(date)}</div>
      <table>${rows}</table>
      <div class="ft">${list.length} article(s) · édité par ecommx solutions</div>
      <script>window.onload=function(){window.print();}</script>
      </body></html>`;
    const w = window.open('', '_blank', 'width=720,height=800');
    if (!w) { push('Autorise les fenêtres pop-up pour imprimer', 'warn'); return; }
    w.document.write(html);
    w.document.close();
  };

  const printThermal = async () => {
    try { await api.printShopping(); push('🖨️ Liste envoyée à l’imprimante'); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  // « Reçu » : l'article a été acheté → +portions au stock (si lié à un plat) puis retiré de la liste.
  const receiveOne = async (i: ShoppingItem) => {
    try {
      const r = await api.receiveShopping(i.id);
      if (r.added > 0) push(`📦 +${r.added} portions · ${r.name} — reste ${r.newQty}`);
      else push(`✅ ${r.name || 'Article'} reçu`);
      setItems((l) => l.filter((x) => x.id !== i.id));
    } catch (e: any) { push(e.message, 'warn'); }
  };
  const receiveAll = async () => {
    try {
      const r = await api.receiveCheckedShopping();
      push(`✅ ${r.count} article(s) reçu(s)${r.portions ? ` · +${r.portions} portions au stock` : ''}`);
      load();
    } catch (e: any) { push(e.message, 'warn'); }
  };

  const checkedCount = items.filter((i) => i.checked).length;
  const categories = Array.from(new Set(items.map((i) => (i.category ?? '').trim()).filter(Boolean))).sort();
  const groups = new Map<string, ShoppingItem[]>();
  items.forEach((i) => { const k = (i.category ?? '').trim() || 'Autres'; if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(i); });
  const groupNames = Array.from(groups.keys()).sort((a, b) => (a === 'Autres' ? 1 : b === 'Autres' ? -1 : a.localeCompare(b)));

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 12px', flexWrap: 'wrap' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>🛒 Liste de courses</div>
        {checkedCount > 0 && <button className="cf-reprint" style={{ width: 'auto', padding: '9px 12px', color: '#fff', background: 'var(--green)', border: 'none' }} onClick={receiveAll}>✅ Tout reçu ({checkedCount})</button>}
        {checkedCount > 0 && <button className="cf-reprint" style={{ width: 'auto', padding: '9px 12px' }} onClick={uncheckAll}>Tout décocher</button>}
        <button className="cf-reprint" style={{ width: 'auto', padding: '9px 12px' }} onClick={printList}>📄 Aperçu / PDF</button>
        <button className="btn-gold" style={{ width: 'auto', padding: '10px 14px' }} onClick={printThermal}>🖨️ Imprimer</button>
      </div>

      <div style={{ fontSize: '.82rem', color: 'var(--ink-faint)', marginBottom: 12, maxWidth: 620 }}>
        Ajoute tes ingrédients (avec catégorie), coche ceux à acheter, puis « Imprimer ». Une fois les courses
        faites, clique <b>✅ Reçu</b> sur un article (ou <b>✅ Tout reçu</b>) : il quitte la liste, et s'il est lié à
        un plat suivi, ses <b>portions sont ajoutées au stock automatiquement</b> (ex. +40).
      </div>

      {/* Liste déroulante des catégories existantes (choisir ou en taper une nouvelle) */}
      <datalist id="shop-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>

      {/* Ajout */}
      <div className="cc" style={{ marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <input className="inp" style={{ flex: '2 1 150px' }} placeholder="Ingrédient (ex. Poulet)" value={name}
          onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <input className="inp" list="shop-cats" style={{ flex: '1 1 120px' }} placeholder="Catégorie (ex. Viande)" value={cat}
          onChange={(e) => setCat(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <input className="inp" style={{ width: 66 }} placeholder="Qté" value={qty} onChange={(e) => setQty(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <input className="inp" style={{ width: 78 }} placeholder="Unité" value={unit} onChange={(e) => setUnit(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <button className="btn-gold" style={{ width: 'auto', padding: '11px 16px' }} onClick={add}>＋ Ajouter</button>
      </div>

      {/* Liste groupée par catégorie */}
      {items.length === 0 && <div className="cc" style={{ fontSize: '.85rem', color: 'var(--ink-faint)' }}>Aucun article. Ajoute ton premier ingrédient ci-dessus.</div>}
      {groupNames.map((g) => (
        <div key={g} className="cc" style={{ marginBottom: 12 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.15rem', marginBottom: 6, color: 'var(--gold)' }}>🗂️ {g} <span style={{ color: 'var(--ink-faint)', fontSize: '.8rem' }}>({groups.get(g)!.length})</span></div>
          {groups.get(g)!.map((i) => (
            <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
              <input type="checkbox" checked={i.checked} style={{ width: 20, height: 20, flexShrink: 0 }}
                onChange={(e) => { patch(i.id, { checked: e.target.checked }); save(i.id, { checked: e.target.checked }); }} />
              {i.auto && <span title="Ajouté automatiquement : stock bas" style={{ fontSize: '.62rem', fontWeight: 800, color: 'var(--cuisine)', background: 'var(--cuisine-bg)', padding: '2px 6px', borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0 }}>⚠️ bas</span>}
              <input className="inp" style={{ flex: '2 1 120px', minWidth: 100, fontWeight: 700, opacity: i.checked ? 1 : 0.7 }} value={i.name}
                onChange={(e) => patch(i.id, { name: e.target.value })} onBlur={(e) => save(i.id, { name: e.target.value })} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <button className="cr-btn" title="Diminuer" onClick={() => step(i, -1)} style={{ fontSize: '1.15rem', lineHeight: 1, minWidth: 34 }}>−</button>
                <input className="inp" style={{ width: 50, textAlign: 'center' }} placeholder="Qté" value={i.quantity}
                  onChange={(e) => patch(i.id, { quantity: e.target.value })} onBlur={(e) => save(i.id, { quantity: e.target.value })} />
                <button className="cr-btn" title="Augmenter" onClick={() => step(i, 1)} style={{ fontSize: '1.15rem', lineHeight: 1, minWidth: 34 }}>＋</button>
              </div>
              <input className="inp" style={{ width: 72 }} placeholder="Unité" value={i.unit}
                onChange={(e) => patch(i.id, { unit: e.target.value })} onBlur={(e) => save(i.id, { unit: e.target.value })} />
              <input className="inp" list="shop-cats" style={{ width: 94 }} placeholder="Catégorie" value={i.category ?? ''}
                onChange={(e) => patch(i.id, { category: e.target.value })} onBlur={(e) => save(i.id, { category: e.target.value })} />
              <button className="cr-btn" title="J'ai reçu cet article (ajoute les portions au stock si c'est un plat suivi)" onClick={() => receiveOne(i)} style={{ color: 'var(--green)', fontWeight: 800 }}>✅ Reçu</button>
              <button className="cr-btn del" title="Supprimer" onClick={() => remove(i.id)}>🗑</button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
